import * as React from "react";
import { styled } from "@mui/material/styles";
import Button from "@mui/material/Button";
import AttachmentIcon from "@mui/icons-material/Attachment";
import { useChat } from "@livekit/components-react";
import FileUploadService from "../../services/fileUpload.service";
import "./FileUpload.css";
import { useRecoilState } from "recoil";
import { fileShareInfosState } from "../../atom/atom";
import useSnackbar from "../../utils/useSnackbar";
import { environment } from "../../environment/environment";
import Tooltip from '@mui/material/Tooltip';

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

export default function FileUpload() {
  const { send } = useChat();
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const { showSnackbar, SnackbarComponent } = useSnackbar();
  const [fileShareInfos, setFileShareInfos] = useRecoilState(fileShareInfosState);
  const supportedFileTypes = `pdf, doc, docx, txt, csv, xls, xlsx, ppt, pptx, jpg, jpeg, png, zip (Max: ${environment.fileSharingConstraints.fileSharingLimit / (1024 * 1024)} MB)`;

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const filesArray = Array.from(files);
      const updatedFileShareInfos = filesArray
        .filter((file) => isFileAllowed(file))
        .map((file) => ({
          fileName: file.name,
          showLoader: true,
        }));
      setFileShareInfos(updatedFileShareInfos);

      for (const file of filesArray.filter((file) => isFileAllowed(file))) {
        try {
          if (!!fileInputRef.current) fileInputRef.current.value = "";
          const downloadInfo: any = await FileUploadService.uploadAsync(file);
          if (!!send) {
            await send(downloadInfo.data.signedUrl);
            showSnackbar({
              message: `${file.name} shared successfully`,
              severity: "success",
            });
          }
          setFileShareInfos((prevFileShareInfos) =>
            prevFileShareInfos.map((info) =>
              info.fileName === file.name
                ? { ...info, showLoader: false }
                : info
            )
          );
        } catch (error) {
          showSnackbar({
            message: "Couldn't upload, please try again.",
            severity: "warning",
          });
          setFileShareInfos((prevFileShareInfos) =>
            prevFileShareInfos.map((info) =>
              info.fileName === file.name
                ? { ...info, showLoader: false }
                : info
            )
          );
        }
      }
    }
  };

  const isFileAllowed = (file: File) => {
    const maxFileSize = environment.fileSharingConstraints.fileSharingLimit;
    const allowedFileTypes = environment.fileSharingConstraints.allowedFileExtensions;
    const fileExtension = file.name.split('.').pop()?.toLowerCase();

    if (file.size > maxFileSize) {
      showSnackbar({
        message: `${file.name} exceeds the maximum file size limit of 20MB.`,
        severity: "warning",
      });
      return false;
    }

    if (!allowedFileTypes.includes(fileExtension || '')) {
      showSnackbar({
        message: `${file.name} has an unsupported file type.`,
        severity: "warning",
      });
      return false;
    }

    return true;
  };

  return (
    <>
      {SnackbarComponent}
      <Tooltip title={`Supported file types: ${supportedFileTypes}`}>
        <Button
          className="fileUploadBtn"
          component="label"
          role={undefined}
          variant="contained"
          tabIndex={-1}
          startIcon={<AttachmentIcon />}
          sx={{ minWidth: "30px" }}
        >
          <VisuallyHiddenInput
            type="file"
            onChange={handleFileUpload}
            ref={fileInputRef}
            multiple
          />
        </Button>
      </Tooltip>
    </>
  );
}
